import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import config from '../config';

function ChangePassword({ onPasswordChanged, isRequired }) {
  const navigate = useNavigate();
  const actualIsRequired = isRequired ?? localStorage.getItem('mustChangePassword') === 'true';
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiBaseUrl}/api/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to change password');
      }

      setSuccess('Password changed successfully! Redirecting to dashboard...');
      localStorage.removeItem('mustChangePassword');

      // Notify parent component
      if (onPasswordChanged) {
        onPasswordChanged();
      }

      // Navigate to dashboard after successful password change
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      setError(err.message || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              sx={{
                bgcolor: 'primary.main',
                borderRadius: '50%',
                p: 1,
                mb: 2
              }}
            >
              <LockResetIcon sx={{ color: 'white', fontSize: 40 }} />
            </Box>
            <Typography component="h1" variant="h5" gutterBottom>
              Change Password
            </Typography>
            {actualIsRequired && (
              <Alert severity="warning" sx={{ mt: 1, mb: 2, width: '100%' }}>
                You must change your password before continuing
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" mb={3}>
              Please enter your current password and choose a new one
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Current Password"
              type="password"
              autoComplete="current-password"
              autoFocus
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={loading || success}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="New Password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="Password must be at least 8 characters long"
              disabled={loading || success}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Confirm New Password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading || success}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || success}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : success ? 'Redirecting...' : 'Change Password'}
              </Button>
              {!actualIsRequired && (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate('/dashboard')}
                  disabled={loading || success}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
}

export default ChangePassword;
